import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import { BellRing, CalendarDays, CalendarOff, CalendarPlus, Check, ChevronLeft, ChevronRight, Copy, Lock, Printer, Settings, Users } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { isAdmin as isAdminRole, isDoctor, canForceSlot, tieneAgente } from '../lib/roles';
import {
  listAgendas,
  listConceptos,
  conceptosElegibles,
  listCitas,
  listBloqueos,
  todayStr,
  addDaysStr,
  strToDate,
  dateToStr,
} from '../lib/citasApi';
import { PageHeader, EmptyState } from '../components/Common';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Skeleton } from '../components/ui/skeleton';
import AgendaGrid from '../components/citas/AgendaGrid';
import QuirofanoDiaBar from '../components/citas/QuirofanoDiaBar';
import CitaCreateDialog from '../components/citas/CitaCreateDialog';
import CitaDetailPopover from '../components/citas/CitaDetailPopover';
import BloqueoDialog from '../components/citas/BloqueoDialog';
import AgenteAskDialog from '../components/AgenteAskDialog';
import { useClinica } from '../lib/clinica';
import RecordatoriosDialog from '../components/citas/RecordatoriosDialog';
import MicButton from '../components/MicButton';
import AgenteIcono from '../components/AgenteIcono';
import BloqueosAgendaDialog from '../components/citas/BloqueosAgendaDialog';
import AgendaConfigDialog from '../components/citas/AgendaConfigDialog';
import PdfPreviewDialog from '../components/PdfPreviewDialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Phase 27 — Sistema de citas: vista de agenda (día / semana laboral).
 *
 * - Selector de agenda (pills; oculto si el usuario solo ve una)
 * - Navegación de fecha (← Hoy →, salto por calendario)
 * - Click en hueco laborable → nueva cita; click en cita → detalle
 * - Admin: bloqueos de agenda y configuración (horario, huecos, overrides)
 */

/** Lunes de la semana de una fecha "YYYY-MM-DD". */
function mondayOf(dateStr) {
  const d = strToDate(dateStr);
  return addDaysStr(dateStr, -((d.getDay() + 6) % 7));
}

function dayLabel(dateStr) {
  const s = strToDate(dateStr).toLocaleDateString('es-ES', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function weekLabel(monday) {
  const from = strToDate(monday);
  const to = strToDate(addDaysStr(monday, 4));
  const fmt = (d, withMonth) =>
    withMonth ? d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }) : String(d.getDate());
  const sameMonth = from.getMonth() === to.getMonth();
  return `${fmt(from, !sameMonth)} – ${fmt(to, true)}`;
}

function AgendaPill({ agenda, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`citas-agenda-pill-${agenda.code || agenda.id}`}
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm whitespace-nowrap transition-colors ${
        selected
          ? 'bg-[#2E6E5E] text-white border-[#2E6E5E] font-medium'
          : 'bg-white text-[#13231E] border-[#dbe3ee] hover:border-[#2E6E5E] hover:bg-[rgba(46,110,94,0.06)]'
      }`}
    >
      <span
        className="w-2.5 h-2.5 rounded-full shrink-0"
        style={{ backgroundColor: agenda.color || '#2E6E5E' }}
        aria-hidden="true"
      />
      {agenda.name}
    </button>
  );
}

export default function CitasPage() {
  const { user } = useAuth();
  const clinicaDatos = useClinica();
  const admin = isAdminRole(user);
  // Gestión de agenda (admin/dirección/administrativo/enfermería): pueden clicar
  // fuera de horario / sobre bloqueos para ajustar el hueco (el backend valida).
  const puedeForzarHueco = canForceSlot(user);
  // Interfaz simplificada para doctores: agenda limpia, sin gestión
  // administrativa (recordatorios, lista de espera).
  const doctor = isDoctor(user);
  // El cuadro del Agente IA, solo a quien tiene el Agente (D7, 22/9/2026:
  // administración, dirección y enfermería). La agenda es la pantalla de
  // entrada del doctor y le ofrecía «Pídele aquí la cita…», que el servidor
  // le contesta con un 403.
  const agente = tieneAgente(user);

  const [agendas, setAgendas] = useState([]);
  const [conceptos, setConceptos] = useState([]);
  const [loadingBase, setLoadingBase] = useState(true);
  // Agendas visibles (1 o varias). Se recuerdan entre pantallas y sesiones:
  // al salir a Recetas y volver, la agenda elegida sigue siendo la misma.
  const [selectedIds, setSelectedIdsRaw] = useState(() => {
    try {
      const v = JSON.parse(localStorage.getItem('cp-agendas-seleccionadas') || '[]');
      return Array.isArray(v) ? v.filter(Boolean) : [];
    } catch { return []; }
  });
  const setSelectedIds = useCallback((valor) => {
    setSelectedIdsRaw((prev) => {
      const next = typeof valor === 'function' ? valor(prev) : valor;
      try { localStorage.setItem('cp-agendas-seleccionadas', JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);
  const [iaOpen, setIaOpen] = useState(false);
  // Cuadro de IA a la vista (2026-07-25): lo escrito aquí abre el chat del
  // Agente IA ya lanzado con la petición. Desde 2026-07-26 es EL MISMO agente
  // del menú y del buscador — antes la agenda tenía su propia IA aparte y
  // había tres sitios distintos donde escribir lo mismo. Propone, tú confirmas.
  const [iaCuadro, setIaCuadro] = useState('');
  const [iaQuery, setIaQuery] = useState('');

  const lanzarIaCuadro = () => {
    const t = iaCuadro.trim();
    if (!t) return;
    setIaQuery(t);
    setIaCuadro('');
    setIaOpen(true);
  };
  const [recordatoriosOpen, setRecordatoriosOpen] = useState(false);
  const [bloqueosOpen, setBloqueosOpen] = useState(false);
  const [date, setDate] = useState(() => todayStr());
  const [view, setView] = useState('dia'); // 'dia' | 'semana'

  const [citas, setCitas] = useState([]);
  const [bloqueos, setBloqueos] = useState([]);
  // Vista "Todas": bloqueos por agenda (cada AgendaGrid recibe SOLO los suyos,
  // porque el grid no filtra bloqueos por agenda).
  const [bloqueosPorAgenda, setBloqueosPorAgenda] = useState({});
  const [loadingData, setLoadingData] = useState(false);
  // La agenda solo se atenúa si la carga tarda de verdad (>250 ms): con la
  // respuesta local típica el fundido no llegaba a completarse y cada ‹ ›
  // era un parpadeo de la rejilla entera (auditoría de animación 2026-08-21).
  const [cargaLenta, setCargaLenta] = useState(false);
  useEffect(() => {
    if (!loadingData) { setCargaLenta(false); return undefined; }
    const t = setTimeout(() => setCargaLenta(true), 250);
    return () => clearTimeout(t);
  }, [loadingData]);

  // Diálogos
  const [createSlot, setCreateSlot] = useState(null); // {date, start} | null
  const [detailCita, setDetailCita] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [bloqueoOpen, setBloqueoOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [calOpen, setCalOpen] = useState(false);
  const [pdfPreview, setPdfPreview] = useState(null); // {url, filename, title} | null
  // Elegir agenda y día cuando hay varias seleccionadas o se está en semana.
  const [imprimirPicker, setImprimirPicker] = useState(null); // {perfil, agendaId, dia} | null
  const [icalOpen, setIcalOpen] = useState(false);
  const [icalUrl, setIcalUrl] = useState('');
  const [icalCopied, setIcalCopied] = useState(false);

  // Multi-selección de agendas: se pueden ver VARIAS a la vez (2026-07-17).
  // - 1 agenda seleccionada → vista día/semana normal (`agenda`).
  // - 2+ seleccionadas → vista "varias a la vez" (solo día): N grids.
  const multiView = selectedIds.length > 1;
  const allView = multiView; // alias interno (varios grids a la vez)

  const agenda = useMemo(
    () => (selectedIds.length === 1 ? agendas.find((a) => a.id === selectedIds[0]) || null : null),
    [agendas, selectedIds],
  );
  const selectedAgendas = useMemo(
    () => agendas.filter((a) => selectedIds.includes(a.id)),
    [agendas, selectedIds],
  );

  // Alternar una agenda en la selección (sin dejar 0 seleccionadas).
  const togglePill = (id) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.length === 1 ? prev : prev.filter((x) => x !== id);
      return [...prev, id];
    });
  };
  // Con varias agendas solo tiene sentido la vista de día.
  useEffect(() => {
    if (selectedIds.length > 1 && view === 'semana') setView('dia');
  }, [selectedIds, view]);

  const monday = useMemo(() => mondayOf(date), [date]);
  const weekDates = useMemo(() => [0, 1, 2, 3, 4].map((i) => addDaysStr(monday, i)), [monday]);

  // Carga inicial: agendas (ya filtradas/ordenadas por rol) + conceptos.
  // Los conceptos van CON los retirados (revisión 2026-09-03): aquí solo
  // sirven para el color y el nombre de cada cita en la rejilla y el resumen,
  // y las citas ya dadas con un concepto retirado tienen que seguir viéndose
  // con su color. Los desplegables de alta
  // cargan los suyos (solo activos).
  useEffect(() => {
    let cancelled = false;
    setLoadingBase(true);
    Promise.all([listAgendas(), listConceptos({ todos: true })])
      .then(([ags, cons]) => {
        if (cancelled) return;
        const list = Array.isArray(ags) ? ags : [];
        setAgendas(list);
        setConceptos(Array.isArray(cons) ? cons : []);
        if (list.length > 0) {
          setSelectedIds((prev) => {
            // La selección recordada puede traer agendas que ya no existen o
            // que este usuario no puede ver: se filtra contra la lista real.
            const validas = prev.filter((id) => list.some((a) => a.id === id));
            return validas.length ? validas : [list[0].id];
          });
        }
      })
      .catch((e) => {
        if (!cancelled) toast.error(e?.response?.data?.detail || 'No se pudieron cargar las agendas');
      })
      .finally(() => {
        if (!cancelled) setLoadingBase(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Contador para refrescar componentes dependientes (barra de quirófano).
  const [reloadKey, setReloadKey] = useState(0);

  // Huecos de cancelaciones con candidatos en espera → badge del botón Espera.
  // Se refresca con cada reload (p. ej. tras cancelar una cita) y al cerrar el panel.

  // Carga de citas + bloqueos del rango visible. Con `silent` refresca en
  // segundo plano: sin atenuar la rejilla ni bloquear clics (lo usa el
  // auto-refresco de los pilotos de estado).
  // Las citas «no acude» se piden también (2026-09-08, punto 12):
  // no desaparecen de la agenda, se quedan en su hueco con el nombre tachado.
  const reload = useCallback(async ({ silent = false } = {}) => {
    if (selectedIds.length === 0) return;
    if (!silent) {
      setReloadKey((k) => k + 1);
      setLoadingData(true);
    }
    try {
      if (multiView) {
        // Varias agendas, vista día: 1 petición de citas (sin agenda_id → el
        // staff ve todas; cada grid filtra las suyas) + bloqueos por agenda.
        const [cs, bsArr] = await Promise.all([
          listCitas({ date, includeNoAcude: true }),
          Promise.all(
            selectedAgendas.map((a) =>
              listBloqueos(a.id, { dateFrom: date, dateTo: date }).catch(() => []),
            ),
          ),
        ]);
        setCitas(Array.isArray(cs) ? cs : []);
        setBloqueosPorAgenda(Object.fromEntries(selectedAgendas.map((a, i) => [a.id, bsArr[i] || []])));
      } else {
        const soloId = selectedIds[0];
        const range =
          view === 'semana'
            ? { dateFrom: monday, dateTo: addDaysStr(monday, 4) }
            : { dateFrom: date, dateTo: date };
        const [cs, bs] = await Promise.all([
          view === 'semana'
            ? listCitas({ agendaId: soloId, dateFrom: range.dateFrom, dateTo: range.dateTo, includeNoAcude: true })
            : listCitas({ agendaId: soloId, date, includeNoAcude: true }),
          listBloqueos(soloId, range),
        ]);
        setCitas(Array.isArray(cs) ? cs : []);
        setBloqueos(Array.isArray(bs) ? bs : []);
      }
    } catch (e) {
      // En silencio no molestamos: si un refresco de fondo falla (wifi, etc.)
      // la agenda se queda como estaba y el siguiente intento lo corrige.
      if (!silent) toast.error(e?.response?.data?.detail || 'No se pudo cargar la agenda');
    } finally {
      if (!silent) setLoadingData(false);
    }
  }, [selectedIds, date, view, monday, multiView, selectedAgendas]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Los sonidos del día viven ahora en AppShell/SonidosVigilante (2026-08-20):
  // suenan en TODA la sesión, no solo con la agenda abierta.
  // Auto-refresco de la agenda: los pilotos (sala de espera, visto por
  // enfermería, luz, LOPD…) los cambian otros compañeros desde sus puestos,
  // así que la vista se pone al día sola cada 30 s — sin parpadeos ni perder
  // lo que tengas abierto — y también al volver a la pestaña.
  useEffect(() => {
    const refrescar = () => {
      if (!document.hidden) reload({ silent: true });
    };
    const id = setInterval(refrescar, 30_000);
    document.addEventListener('visibilitychange', refrescar);
    window.addEventListener('focus', refrescar);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', refrescar);
      window.removeEventListener('focus', refrescar);
    };
  }, [reload]);

  const step = view === 'semana' ? 7 : 1;


  // Enlace iCal del doctor (sus agendas) para suscribirse desde Google/Apple
  // Calendar. El backend crea el token la primera vez.
  const abrirIcal = async () => {
    setIcalOpen(true);
    setIcalCopied(false);
    if (icalUrl) return;
    try {
      const res = await api.get('/api/citas/ical-link');
      setIcalUrl(res.data.url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo generar el enlace');
      setIcalOpen(false);
    }
  };

  const copiarIcal = async () => {
    try {
      await navigator.clipboard.writeText(icalUrl);
      setIcalCopied(true);
      toast.success('Enlace copiado');
    } catch {
      toast.error('No se pudo copiar — selecciona el texto y cópialo a mano');
    }
  };

  const [icalBusy, setIcalBusy] = useState(false);
  // Añadir = abre Google Calendar con la suscripción. Reactiva por si estaba quitada.
  const anadirIcal = async () => {
    try { await api.post('/api/citas/ical-active', null, { params: { active: true } }); } catch { /* no bloqueante */ }
    window.open(`https://calendar.google.com/calendar/render?cid=${encodeURIComponent(icalUrl)}`, '_blank', 'noopener');
  };
  // Quitar = pausa el feed; Google vacía la suscripción en su próxima sync.
  const quitarIcal = async () => {
    setIcalBusy(true);
    try {
      await api.post('/api/citas/ical-active', null, { params: { active: false } });
      toast.success('Quitado de Google Calendar. Puede tardar unas horas en desaparecer allí.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo quitar');
    } finally {
      setIcalBusy(false);
    }
  };

  // Imprimir el parte del día. Con UNA agenda y en vista de día sale directo;
  // con varias agendas, o en vista de semana, hay que preguntar cuál y qué día
  // (antes el botón simplemente no aparecía en esos casos).
  const pdfDelDia = useCallback((ag, dia, perfil) => {
    const sufijo = perfil ? `&perfil=${perfil}` : '';
    const nombre = (ag.name || 'dia').replace(/ /g, '_');
    setPdfPreview({
      url: `${BACKEND_URL}/api/citas/agenda-dia/pdf?agenda_id=${ag.id}&date=${dia}${sufijo}`,
      filename: `${perfil ? 'agenda_enfermeria' : 'agenda'}_${nombre}_${dia}.pdf`,
      title: `${perfil ? clinicaDatos.agenda_perfil_enfermeria : 'Agenda del día'} · ${ag.name} · ${dia}`,
    });
  }, [clinicaDatos.agenda_perfil_enfermeria]);

  const abrirImpresion = useCallback((perfil) => {
    if (selectedAgendas.length === 1 && view === 'dia') {
      pdfDelDia(selectedAgendas[0], date, perfil);
      return;
    }
    setImprimirPicker({ perfil, agendaId: selectedAgendas[0]?.id || '', dia: date });
  }, [selectedAgendas, view, date, pdfDelDia]);

  const onSlotClick = ({ date: d, start }) => setCreateSlot({ date: d, start });
  const onCitaClick = (cita) => {
    setDetailCita(cita);
    setDetailOpen(true);
  };

  // Abrir una cita concreta desde fuera de la agenda:
  //     /citas?cita=<id>&fecha=<YYYY-MM-DD>&agenda=<id>
  // Lo usa la relación de citas de la ficha del paciente
  // (2026-09-09): lleva al día de esa cita, con su agenda seleccionada y el
  // resumen ya desplegado — que es como se ve el detalle de una cita aquí.
  // Detalles que importan:
  //  - Solo la primera vez, y la dirección se limpia: recargar o volver atrás
  //    no vuelve a abrir el resumen (mismo criterio que /informes?edit=<id>).
  //  - La cita se pide APARTE, en una petición propia, en vez de esperar a la
  //    rejilla: al cambiar de día y de agenda hay dos cargas en vuelo y no se
  //    sabe cuál contesta antes; así el resumen sale con la cita correcta.
  //  - Si la agenda no es de las que ve este usuario, o la cita ya no está
  //    (anulada, borrada), se avisa y la agenda se queda como estaba: nunca
  //    una pantalla en blanco ni un error suelto.
  const [searchParams, setSearchParams] = useSearchParams();
  const citaParamHecho = useRef(false);
  useEffect(() => {
    if (citaParamHecho.current || loadingBase) return;
    const citaId = searchParams.get('cita');
    if (!citaId) return;
    citaParamHecho.current = true;
    const fecha = searchParams.get('fecha') || todayStr();
    const agendaId = searchParams.get('agenda') || '';
    setSearchParams({}, { replace: true });
    if (agendaId && !agendas.some((a) => a.id === agendaId)) {
      toast.error('Esa cita está en una agenda que no puedes ver');
      return;
    }
    setView('dia');
    setDate(fecha);
    if (agendaId) setSelectedIds([agendaId]);
    listCitas({ agendaId: agendaId || undefined, date: fecha, includeNoAcude: true })
      .then((cs) => {
        const encontrada = (Array.isArray(cs) ? cs : []).find((c) => c.id === citaId);
        if (!encontrada) {
          toast.error('Esa cita ya no está en la agenda: puede que se anulara');
          return;
        }
        setDetailCita(encontrada);
        setDetailOpen(true);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || 'No se pudo abrir la cita'));
  }, [loadingBase, agendas, searchParams, setSearchParams, setSelectedIds]);

  // El resumen de cita se veía con los datos del clic: cambiabas algo y había
  // que cerrarlo para comprobar si había entrado (2026-07-28). Ahora la cita
  // que enseña es la de la lista, y cuando se guarda algo se sustituye en el
  // sitio: se ve al momento arriba y en la rejilla, sin recargar.
  const citaViva = useMemo(
    () => citas.find((c) => c.id === detailCita?.id) || detailCita,
    [citas, detailCita],
  );
  const onCitaActualizada = useCallback((actualizada) => {
    if (actualizada?.id) {
      setCitas((prev) => prev.map((c) => (c.id === actualizada.id ? actualizada : c)));
      setDetailCita((prev) => (prev?.id === actualizada.id ? actualizada : prev));
    }
    // Y por detrás, la recarga de siempre: al cambiar de día o de agenda la
    // cita ya no está en esta lista, y hay que traerse la agenda entera.
    reload({ silent: true });
  }, [reload]);

  // Empty state elegante: la agenda no abre ese día y no hay nada que pintar.
  const dayHasHours = (dstr) =>
    (agenda?.working_hours?.[String((strToDate(dstr).getDay() + 6) % 7)] || []).length > 0;
  const showDayEmpty =
    view === 'dia' && agenda && !dayHasHours(date) && citas.filter((c) => c.date === date).length === 0;
  const showWeekEmpty =
    view === 'semana' && agenda && !weekDates.some(dayHasHours) && citas.length === 0;

  const subtitle = multiView
    ? `${selectedIds.length} agendas a la vez`
    : agenda
      ? `${agenda.name}${agenda.doctor_name ? ` · ${agenda.doctor_name}` : ''}`
      : 'Agenda de consultas de la clínica';

  // Contador de pacientes del día visible (en las agendas seleccionadas).
  const pacientesDia = useMemo(() => {
    const activa = (c) => !['cancelada', 'cancelled', 'no_acude'].includes(c.status);
    return citas.filter(
      (c) => c.date === date && activa(c) && (multiView ? selectedIds.includes(c.agenda_id) : true),
    ).length;
  }, [citas, date, multiView, selectedIds]);

  return (
    <div>
      <PageHeader title="Agenda" subtitle={subtitle} />

      {/* Fila de controles sticky */}
      <div className="sticky top-0 z-30 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-2.5 bg-white/95 backdrop-blur border-b border-[#D9E3DD] mb-4">
        {/* Fila de arriba (2026-07-26): SOLO qué agenda estás mirando. Bloqueo y
            Gestión de agendas iban aquí al lado y se leían como una agenda más;
            bajan con el resto de herramientas. */}
        {agendas.length > 1 && (
          <div className="flex items-center gap-1.5 overflow-x-auto pr-1 mb-2" data-testid="citas-agenda-pills">
            {agendas.map((a) => (
              <AgendaPill
                key={a.id}
                agenda={a}
                selected={selectedIds.includes(a.id)}
                onClick={() => togglePill(a.id)}
              />
            ))}
            {/* Todas las agendas a la vez (solo vista día). Es un TOGGLE: si ya
                están todas seleccionadas, al pulsarla vuelve a una sola (así no
                hay que quitarlas de una en una). Toca una pill suelta para
                añadir/quitar; se pueden ver varias simultáneamente. */}
            <AgendaPill
              agenda={{ id: 'todas', code: 'todas', name: 'Todas', color: '#13231E' }}
              selected={agendas.length > 1 && selectedIds.length === agendas.length}
              onClick={() => setSelectedIds((prev) => (
                prev.length === agendas.length ? [agendas[0].id] : agendas.map((a) => a.id)
              ))}
            />
          </div>
        )}

        {/* Fila de abajo: a la izquierda el día que miras, a la derecha las
            herramientas de ese día. */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Navegación de fecha */}
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9"
              onClick={() => setDate(addDaysStr(date, -step))}
              aria-label="Anterior"
              data-testid="citas-date-prev"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              className="h-9"
              onClick={() => setDate(todayStr())}
              data-testid="citas-date-today"
            >
              Hoy
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9"
              onClick={() => setDate(addDaysStr(date, step))}
              aria-label="Siguiente"
              data-testid="citas-date-next"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>

            <Popover open={calOpen} onOpenChange={setCalOpen}>
              <PopoverTrigger asChild>
                <Button variant="ghost" className="h-9 font-medium text-[#13231E]" data-testid="citas-date-label">
                  <CalendarDays className="w-4 h-4 mr-2 text-[#2E6E5E]" />
                  {view === 'semana' ? weekLabel(monday) : dayLabel(date)}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  locale={es}
                  selected={strToDate(date)}
                  defaultMonth={strToDate(date)}
                  onSelect={(d) => {
                    if (!d) return;
                    setDate(dateToStr(d));
                    setCalOpen(false);
                  }}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Toggle Día / Semana (en "Todas" solo hay vista de día) */}
          {!allView && (
            <Tabs value={view} onValueChange={setView}>
              <TabsList className="h-9" data-testid="citas-view-toggle">
                <TabsTrigger value="dia" data-testid="citas-view-dia">
                  Día
                </TabsTrigger>
                <TabsTrigger value="semana" data-testid="citas-view-semana">
                  Semana
                </TabsTrigger>
              </TabsList>
            </Tabs>
          )}

          {/* Herramientas del día, todas juntas a la derecha y siempre en el
              mismo orden (2026-07-26): antes iban sueltas en la fila y cambiaban
              de sitio según qué botones tocaran a cada usuario.
              El botón "IA" se quitó: el cuadro de abajo y el Agente IA del menú
              abren ya ese mismo chat. */}
          {/* En móvil, cuadrícula de 2 columnas con botones del mismo ancho:
              empaquetados y sin huecos (2026-08-30). En pantalla
              grande, fila flexible a la derecha como siempre. */}
          <div className="grid grid-cols-2 gap-1.5 w-full sm:flex sm:flex-wrap sm:items-center sm:w-auto sm:ml-auto">
          {/* Recordatorios de cita (Phase 27.17) — gestión, no para doctores */}
          {!doctor && (
          <Button
            variant="outline"
            className="h-8 px-2 text-xs sm:h-9 sm:px-4 sm:text-sm"
            onClick={() => setRecordatoriosOpen(true)}
            data-testid="citas-recordatorios"
          >
            <BellRing className="w-4 h-4 mr-1.5 text-[#2E6E5E]" /> Recordatorios
          </Button>
          )}

          {/* Agenda del día imprimible.
              OJO con la condición: antes era `agenda && view === 'dia'`, y
              `agenda` vale null en cuanto hay DOS agendas seleccionadas. El
              botón desaparecía sin decir nada — y como la selección se guarda
              en el navegador de cada uno, a unos les salía y a otros no, con
              el mismo permiso (2026-07-28). Ahora se ve
              siempre que haya alguna agenda, y si hay varias (o estás en
              semana) se pregunta cuál y qué día. */}
          {selectedAgendas.length > 0 && (
            <Button
              variant="outline"
              className="h-8 px-2 text-xs sm:h-9 sm:px-4 sm:text-sm"
              onClick={() => abrirImpresion(null)}
              data-testid="citas-imprimir-dia"
            >
              <Printer className="w-4 h-4 mr-1.5 text-[#2E6E5E]" /> Imprimir
            </Button>
          )}

          {/* Bloquear agendas por días: festivos, vacaciones, congresos…
              (2026-08-26). Gestión, no doctores. */}
          {!doctor && (
            <Button
              variant="outline"
              className="h-8 px-2 text-xs sm:h-9 sm:px-4 sm:text-sm"
              onClick={() => setBloqueosOpen(true)}
              data-testid="citas-agendas-bloqueadas"
            >
              <CalendarOff className="w-4 h-4 mr-1.5 text-[#1d4ed8]" /> Agendas bloqueadas
            </Button>
          )}

          {/* Perfil de enfermería: exporta de la agenda del doctor solo las
              primeras valoraciones y primeras revisiones (Phase 27.22). El
              rótulo lo pone cada clínica en su configuración. */}
          {/* El botón «Agenda de enfermería» se jubila (2026-08-20): la agenda de
              Enfermería ya ENSEÑA e IMPRIME su día completo — sus citas más
              las de su perfil en otras consultas. Se abre y se imprime. */}

          {/* Suscripción del doctor a su agenda desde Google/Apple Calendar */}
          {user?.agenda_id && (
            <Button
              variant="outline"
              className="h-8 px-2 text-xs sm:h-9 sm:px-4 sm:text-sm"
              onClick={abrirIcal}
              data-testid="citas-google-calendar"
            >
              <CalendarPlus className="w-4 h-4 mr-1.5 text-[#2E6E5E]" /> Google Calendar
            </Button>
          )}

          {/* Lista de espera retirada de la interfaz (2026-08-29:
              no se usaba). El backend sigue; para recuperarla, git. */}

          {/* Acciones de admin, al final y detrás de una raya: tocan cómo está
              montada la agenda, no el día que estás mirando. */}
          {admin && agenda && (
            <>
              <span className="hidden sm:block w-px h-6 bg-[#D9E3DD] mx-0.5" aria-hidden="true" />
              <Button
                variant="outline"
                className="h-8 px-2 text-xs sm:h-9 sm:px-4 sm:text-sm"
                onClick={() => setBloqueoOpen(true)}
                data-testid="citas-new-bloqueo"
              >
                <Lock className="w-4 h-4 mr-1.5" /> Bloqueo
              </Button>
              <Button
                variant="outline"
                className="h-8 px-2 text-xs sm:h-9 sm:w-9 sm:p-0 sm:text-sm"
                onClick={() => setConfigOpen(true)}
                aria-label="Configurar agenda"
                data-testid="citas-config-agenda"
              >
                <Settings className="w-4 h-4" />
                <span className="ml-1.5 sm:hidden">Configurar</span>
              </Button>
              <Button variant="outline" className="h-8 px-2 text-xs sm:h-9 sm:px-4 sm:text-sm" asChild data-testid="citas-gestion-agendas-link">
                <Link to="/gestion-agendas">Gestión de agendas</Link>
              </Button>
            </>
          )}
          </div>
        </div>
      </div>

      {/* Cuadro de IA en una sola línea (2026-08-30: solo «Agente IA»
          + escribir o dictar; el texto explicativo sobraba). Solo a quien
          tiene el Agente (D7, 22/9/2026): al doctor no se le ofrece. */}
      {agente && (
      <div className="border border-[#2E6E5E]/40 bg-[#2E6E5E]/[0.04] rounded-lg p-2 mb-3 flex items-center gap-2"
        data-testid="citas-ia-cuadro">
        <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#13231E] shrink-0 pl-1">
          <AgenteIcono size={24} />
          Agente IA
        </span>
        <Input
          value={iaCuadro}
          onChange={(e) => setIaCuadro(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') lanzarIaCuadro(); }}
          placeholder="Pídele aquí la cita…"
          className="h-9 flex-1 min-w-0 bg-white dark:bg-card"
          data-testid="citas-ia-cuadro-input"
        />
        <MicButton onText={(t) => setIaCuadro((prev) => (prev ? `${prev} ${t}` : t))} />
        <Button onClick={lanzarIaCuadro} disabled={!iaCuadro.trim()}
          className="h-9 bg-[#2E6E5E] hover:bg-[#1E4A3F] shrink-0"
          title="Pedírselo a la IA de la agenda"
          data-testid="citas-ia-cuadro-enviar">
          <AgenteIcono size={20} />
        </Button>
      </div>
      )}

      {/* Contador de pacientes del día (encima de la agenda) */}
      <div className="flex items-center gap-2 mb-3" data-testid="citas-contador-pacientes">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[rgba(46,110,94,0.10)] border border-[#2E6E5E]/25 px-3 py-1 text-sm">
          <Users className="w-4 h-4 text-[#2E6E5E]" />
          <strong className="text-[#13231E] tabular-nums">{pacientesDia}</strong>
          <span className="text-[#13231E]/80">paciente{pacientesDia === 1 ? '' : 's'} este día</span>
        </span>
        {multiView && (
          <span className="text-xs text-muted-foreground">en {selectedIds.length} agendas</span>
        )}
      </div>

      {/* Contenido */}
      {loadingBase ? (
        <div className="space-y-3">
          <Skeleton className="h-9 w-72 rounded-md" />
          <Skeleton className="h-[420px] w-full rounded-xl" />
        </div>
      ) : agendas.length === 0 ? (
        <EmptyState
          icon={CalendarOff}
          title="No hay agendas disponibles"
          description="Todavía no se ha configurado ninguna agenda de citas. Contacta con administración."
        />
      ) : showDayEmpty ? (
        <EmptyState
          icon={CalendarOff}
          title="Sin consulta este día"
          description={`La agenda ${agenda.name} no tiene horario configurado para el ${dayLabel(date).toLowerCase()}.`}
          action={
            <Button variant="outline" onClick={() => setDate(todayStr())}>
              Ir a hoy
            </Button>
          }
        />
      ) : showWeekEmpty ? (
        <EmptyState
          icon={CalendarOff}
          title="Sin consulta esta semana"
          description={`La agenda ${agenda.name} no tiene horario configurado entre semana para estas fechas.`}
          action={
            <Button variant="outline" onClick={() => setDate(todayStr())}>
              Ir a hoy
            </Button>
          }
        />
      ) : allView ? (
        /* Varias agendas a la vez (vista día): una columna por agenda seleccionada. */
        <div className={cargaLenta ? 'opacity-60 pointer-events-none transition-opacity' : 'transition-opacity'}>
          <div className="flex gap-3 overflow-x-auto pb-2" data-testid="citas-todas-grid">
            {selectedAgendas.map((a) => (
              <div key={a.id} className="flex-1 min-w-[280px]">
                <div className="flex items-center gap-1.5 mb-1 px-1">
                  <span
                    className="w-2.5 h-2.5 rounded-full inline-block shrink-0"
                    style={{ backgroundColor: a.color || '#2E6E5E' }}
                  />
                  <span className="text-sm font-semibold text-[#13231E] truncate">{a.name}</span>
                </div>
                <AgendaGrid
                  agenda={a}
                  date={date}
                  citas={citas.filter((c) => c.agenda_id === a.id
                    // La agenda de enfermería enseña TAMBIÉN los pacientes de
                    // su perfil citados en otras consultas (2026-08-20). Cuáles
                    // lo decide el servidor en `perfil_enfermeria`, no esta
                    // pantalla: desde el 24/9/2026, solo los de las agendas
                    // con consulta conjunta.
                    || (a.es_enfermeria && c.perfil_enfermeria && c.agenda_id !== a.id))}
                  bloqueos={bloqueosPorAgenda[a.id] || []}
                  conceptos={conceptos}
                  agendas={agendas}
                  onSlotClick={({ date: d, start }) => setCreateSlot({ date: d, start, agendaId: a.id })}
                  onCitaClick={onCitaClick}
                  isAdmin={puedeForzarHueco}
                  compact
                />
              </div>
            ))}
          </div>
        </div>
      ) : agenda ? (
        <div className={cargaLenta ? 'opacity-60 pointer-events-none transition-opacity' : 'transition-opacity'}>
          {view === 'dia' ? (
            <div className="space-y-2">
              <QuirofanoDiaBar
                agenda={agenda}
                date={date}
                refreshKey={reloadKey}
                onChanged={reload}
              />
              <AgendaGrid
                agenda={agenda}
                date={date}
                citas={citas}
                bloqueos={bloqueos}
                conceptos={conceptos}
                agendas={agendas}
                onSlotClick={onSlotClick}
                onCitaClick={onCitaClick}
                isAdmin={puedeForzarHueco}
              />
            </div>
          ) : (
            <AgendaGrid
              agenda={agenda}
              dates={weekDates}
              citas={citas}
              bloqueos={bloqueos}
              conceptos={conceptos}
              agendas={agendas}
              onSlotClick={onSlotClick}
              onCitaClick={onCitaClick}
              isAdmin={puedeForzarHueco}
              compact
              showHeaders
              onDateHeaderClick={(d) => {
                setDate(d);
                setView('dia');
              }}
            />
          )}
        </div>
      ) : null}

      {/* Diálogos. En la vista "Todas" la agenda del diálogo se resuelve por
          la columna pulsada (crear) o por la propia cita (detalle). */}
      <CitaCreateDialog
        open={!!createSlot}
        onOpenChange={(v) => {
          if (!v) setCreateSlot(null);
        }}
        agenda={allView ? agendas.find((a) => a.id === createSlot?.agendaId) || null : agenda}
        defaultDate={createSlot?.date}
        defaultStart={createSlot?.start}
        onCreated={reload}
      />

      <CitaDetailPopover
        open={detailOpen}
        onOpenChange={setDetailOpen}
        cita={citaViva}
        conceptos={conceptos}
        agendas={agendas}
        agenda={allView ? agendas.find((a) => a.id === citaViva?.agenda_id) || null : agenda}
        onChanged={onCitaActualizada}
      />

      {/* El Agente IA, el mismo del menú y del buscador. Es de
          administración, dirección y enfermería (D7, 22/9/2026): el servidor
          (auth.require_agente) le contesta 403 al doctor y al anestesista en
          /api/agente/preguntar, así que a ellos ni se les monta. Dentro, las
          herramientas siguen filtrando por los permisos de cada uno. */}
      {agente && (
        <AgenteAskDialog open={iaOpen} onOpenChange={(v) => { setIaOpen(v); if (!v) setIaQuery(''); }}
          initialQuery={iaQuery} onCitaCambiada={reload} />
      )}

      {/* Recordatorios y Espera: sus botones los ve todo el personal menos los
          doctores, así que los diálogos se montan con la MISMA condición. Antes
          estaban dentro del bloque de admin y para el resto el botón no hacía
          nada al pulsarlo. */}
      {!doctor && (
        <>
          {/* Con el día y la agenda que se están viendo (2026-09-03,
              punto 3): el panel manda a «todas las citas de la agenda que
              esté seleccionada en ese momento». Con varias a la vez, el
              panel arranca en la primera y deja cambiar. */}
          <RecordatoriosDialog
            open={recordatoriosOpen}
            onOpenChange={setRecordatoriosOpen}
            onChanged={reload}
            agendas={selectedAgendas}
            date={date}
          />
          <BloqueosAgendaDialog open={bloqueosOpen} onOpenChange={setBloqueosOpen} agendas={agendas} onChanged={reload} />
        </>
      )}

      {admin && (
        <>
          <BloqueoDialog
            open={bloqueoOpen}
            onOpenChange={setBloqueoOpen}
            agenda={agenda}
            defaultDate={date}
            onSaved={reload}
          />
          <AgendaConfigDialog
            open={configOpen}
            onOpenChange={setConfigOpen}
            agenda={agenda}
            conceptos={conceptosElegibles(conceptos)}
            onSaved={(updated) => {
              if (updated?.id) {
                setAgendas((prev) => prev.map((a) => (a.id === updated.id ? { ...a, ...updated } : a)));
              } else {
                listAgendas().then((ags) => setAgendas(Array.isArray(ags) ? ags : [])).catch(() => {});
              }
              reload();
            }}
          />
        </>
      )}

      <PdfPreviewDialog
        open={!!pdfPreview}
        onOpenChange={(o) => !o && setPdfPreview(null)}
        url={pdfPreview?.url}
        filename={pdfPreview?.filename}
        title={pdfPreview?.title}
      />

      {/* Qué agenda y qué día imprimir, cuando no hay una sola respuesta obvia. */}
      <Dialog open={!!imprimirPicker} onOpenChange={(o) => !o && setImprimirPicker(null)}>
        <DialogContent className="max-w-sm" data-testid="citas-imprimir-picker">
          <DialogHeader>
            <DialogTitle>
              {imprimirPicker?.perfil ? clinicaDatos.agenda_perfil_enfermeria : 'Imprimir el parte del día'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Agenda</label>
              <select
                className="w-full mt-1 h-9 rounded-md border border-input bg-background px-3 text-sm"
                value={imprimirPicker?.agendaId || ''}
                onChange={(e) => setImprimirPicker((p) => ({ ...p, agendaId: e.target.value }))}
                data-testid="citas-imprimir-agenda"
              >
                {selectedAgendas.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Día</label>
              <Input
                type="date"
                className="mt-1 h-9"
                value={imprimirPicker?.dia || date}
                onChange={(e) => setImprimirPicker((p) => ({ ...p, dia: e.target.value }))}
                data-testid="citas-imprimir-dia-input"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setImprimirPicker(null)}>Cancelar</Button>
            <Button
              className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
              disabled={!imprimirPicker?.agendaId || !imprimirPicker?.dia}
              onClick={() => {
                const ag = selectedAgendas.find((a) => a.id === imprimirPicker.agendaId);
                if (!ag) return;
                pdfDelDia(ag, imprimirPicker.dia, imprimirPicker.perfil);
                setImprimirPicker(null);
              }}
              data-testid="citas-imprimir-confirmar"
            >
              <Printer className="w-4 h-4 mr-1.5" /> Ver el parte
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Enlace de suscripción a Google/Apple Calendar */}
      <Dialog open={icalOpen} onOpenChange={setIcalOpen}>
        <DialogContent className="max-w-lg" data-testid="citas-ical-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#13231E]">
              <CalendarPlus className="w-5 h-5 text-[#2E6E5E]" /> Tu agenda en Google Calendar
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-[#13231E]">
            <p className="text-muted-foreground">
              Añade tu agenda a Google Calendar y se mantiene al día sola. Por privacidad,
              solo se envía el <strong className="text-[#13231E]">nombre del paciente y las notas</strong> —
              ningún otro dato. No compartas el enlace: es personal.
            </p>
            <div className="flex items-center gap-2">
              <Input
                readOnly
                value={icalUrl || 'Generando enlace…'}
                onFocus={(e) => e.target.select()}
                className="text-xs"
                data-testid="citas-ical-url"
              />
              <Button variant="outline" size="icon" className="shrink-0" onClick={copiarIcal} disabled={!icalUrl}>
                {icalCopied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
                disabled={!icalUrl || icalBusy}
                onClick={anadirIcal}
                data-testid="citas-ical-google"
              >
                <CalendarPlus className="w-4 h-4 mr-1.5" /> Añadir a Google Calendar
              </Button>
              <Button
                variant="outline"
                className="text-red-700 border-red-200 hover:bg-red-50 hover:text-red-800"
                disabled={!icalUrl || icalBusy}
                onClick={quitarIcal}
                data-testid="citas-ical-remove"
              >
                <CalendarOff className="w-4 h-4 mr-1.5" /> Quitar de Google Calendar
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              «Quitar» vacía tu agenda en Google (se mantiene el enlace por si la vuelves a añadir).
              Google sincroniza cada pocas horas, así que los cambios —añadir o quitar— pueden tardar
              un poco en verse. En móvil o Apple Calendar: copia el enlace y usa «Añadir/Suscribir calendario desde URL».
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
